/**
 * ReceivingBankAccountSelect — dropdown de contas de recebimento (billing REST).
 * Substitui o enum hardcoded `BankAccount`. Value = aliasName (chave aceita pelo backend,
 * legível); label = exibitionName. Envia-se aliasName como receiving_bank_account_id.
 * Lista apenas contas ATIVAS: uma conta desativada segue válida para pagamentos já
 * registrados, mas não pode ser escolhida em um pagamento novo.
 *
 * ⚠️ Nasce SEM seleção ("Selecione uma conta..."): quem registra o pagamento tem de escolher. A
 * primeira da lista vinha pré-selecionada, e um pagamento que entrou em outra conta era gravado
 * na primeira sem ninguém perceber. Por isso quem usa este select precisa recusar o envio sem
 * conta — os modais de pagamento fazem isso.
 *
 * Renderiza pelo `SelectTextField` do sistema (rótulo flutuante, mesmo estilo dos demais
 * campos) — antes era um `<select>` cru que destoava da linha ao lado.
 */
import { useEffect, useState } from 'react';
import { listReceivingBankAccounts, ReceivingBankAccount } from '../../pages/billing/services/receivingBankAccountApi';
import SelectTextField from '../SelectTextField';

interface Props {
    value: string;
    onChange: (aliasName: string) => void;
    className?: string;
    id?: string;
    label?: string;
}

const ReceivingBankAccountSelect: React.FC<Props> = ({ value, onChange, className, id, label = 'Conta de Recebimento' }) => {
    const [accounts, setAccounts] = useState<ReceivingBankAccount[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const list = await listReceivingBankAccounts();
                if (!active) return;
                setAccounts(list);
                // Valor que não está entre as ATIVAS (conta desativada) é limpo, e não trocado pela
                // primeira: o select mostraria "Selecione" enquanto o formulário seguiria enviando
                // a conta antiga.
                if (value && !list.some(a => a.aliasName === value)) {
                    onChange('');
                }
            } catch {
                if (active) setAccounts([]);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const emptyLabel = loading
        ? 'Carregando…'
        : accounts.length === 0 ? 'Nenhuma conta cadastrada' : 'Selecione uma conta...';

    return (
        <SelectTextField
            id={id}
            label={label}
            className={className}
            value={value}
            disabled={loading}
            isLoading={loading}
            emptyValue=""
            emptyLabel={emptyLabel}
            items={accounts.map(acc => ({ value: acc.aliasName, label: acc.exibitionName }))}
            onChange={e => onChange(e.target.value)}
        />
    );
};

export default ReceivingBankAccountSelect;
